import type { CaptureFailureCode } from 'ts-components/PiChat/protocol';
import {
  afterPaint,
  getDashletRender,
} from 'ts-components/CustomBlockRender/dashletRenderRegistry';
import type { CaptureOutcome } from './captureUpload';

const MAX_CAPTURE_EDGE_PX = 1568;
const MAX_PIXEL_RATIO = 2;
const NOT_RENDERED_GRACE_MS = 2_000;

const EXCLUDED_CAPTURE_SELECTORS = [
  '.dashlet-menu',
  '.drag-handle',
  '.react-resizable-handle',
] as const;
const EXCLUDED_SELECTOR = EXCLUDED_CAPTURE_SELECTORS.join(', ');

const captureFilter = (node: Node): boolean => {
  const element = node as Partial<Element>;
  return (
    typeof element.matches !== 'function' || !element.matches(EXCLUDED_SELECTOR)
  );
};

class CaptureError extends Error {
  constructor(
    readonly code: CaptureFailureCode,
    message: string
  ) {
    super(message);
    this.name = 'CaptureError';
  }
}

const captureScale = (cssWidth: number, cssHeight: number) => {
  const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
  const longEdge = Math.max(cssWidth, cssHeight);
  return longEdge * ratio > MAX_CAPTURE_EDGE_PX
    ? MAX_CAPTURE_EDGE_PX / longEdge
    : ratio;
};

const nextPaint = () => new Promise<void>((resolve) => afterPaint(resolve));

const findDashletCard = (element: HTMLElement, dashletId: string) => {
  const card = element.closest<HTMLElement>('[data-dashlet-id]');
  return card?.getAttribute('data-dashlet-id') === dashletId ? card : element;
};

const waitForSettledElement = async (
  dashletId: string,
  signal: AbortSignal
): Promise<HTMLElement> => {
  let missingSince = Date.now();
  while (!signal.aborted) {
    const entry = getDashletRender(dashletId);
    if (entry?.element.isConnected) {
      if (entry.settled) return entry.element;
      missingSince = Date.now();
    } else if (Date.now() - missingSince >= NOT_RENDERED_GRACE_MS) {
      throw new CaptureError(
        'not_rendered',
        `Dashlet ${dashletId} is not rendered on this page`
      );
    }
    await nextPaint();
  }
  throw new CaptureError('capture_failed', 'Capture was cancelled');
};

const finishAnimations = (card: HTMLElement) => {
  card.getAnimations({ subtree: true }).forEach((animation) => {
    try {
      animation.finish();
    } catch {
      // infinite animation
    }
  });
};

export const captureDashletForRequest = async (
  dashletId: string,
  {
    signal,
    reply,
    waitForRefresh,
  }: {
    signal: AbortSignal;
    reply: (outcome: CaptureOutcome) => void;
    waitForRefresh: () => Promise<unknown>;
  }
): Promise<void> => {
  try {
    await waitForRefresh().catch(() => undefined);
    const card = findDashletCard(
      await waitForSettledElement(dashletId, signal),
      dashletId
    );
    await Promise.all([
      ...Array.from(card.querySelectorAll('img')).map((image) =>
        image.decode().catch(() => undefined)
      ),
      document.fonts.ready,
    ]);
    const rect = card.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      throw new CaptureError(
        'not_rendered',
        `Dashlet ${dashletId} has no size (hidden?)`
      );
    }
    finishAnimations(card);
    const { domToBlob } = await import('modern-screenshot');
    const blob = await domToBlob(card, {
      scale: captureScale(rect.width, rect.height),
      filter: captureFilter,
      type: 'image/png',
    });
    reply({ ok: true, blob });
  } catch (error) {
    reply({
      ok: false,
      code: error instanceof CaptureError ? error.code : 'capture_failed',
      message: error instanceof Error ? error.message : String(error),
    });
  }
};
